import { useEffect, useRef } from "react";
import { useUser } from "@/features/auth/hooks/useUser";
import { applyLanguage, browserStorage, getLanguage, isSavePending } from "@/i18n";
import {
	STORAGE_KEY,
	accountLanguageToApply,
	isEnglishEnabled,
	isSupportedLanguage,
	writeStoredLanguage,
} from "@/i18n/language";

// Applies the account's language when the signed-in user changes, and follows
// a switch made in another tab of this browser. Renders nothing.
export default function LanguageSync() {
	const { user } = useUser();
	const previousUserKey = useRef(null);
	const enabled = isEnglishEnabled(import.meta.env);

	useEffect(() => {
		if (!enabled) return;

		const { userKey, language, remember } = accountLanguageToApply({
			previousUserKey: previousUserKey.current,
			user,
			savePending: isSavePending(),
			current: getLanguage(),
		});
		previousUserKey.current = userKey;
		if (language) applyLanguage(language);
		// Also when it already matches: see accountLanguageToApply.
		if (remember) writeStoredLanguage(browserStorage(), remember);
	}, [enabled, user]);

	useEffect(() => {
		if (!enabled) return;

		function followOtherTab(event) {
			if (event.key !== STORAGE_KEY) return;
			if (!isSupportedLanguage(event.newValue)) return;
			if (event.newValue === getLanguage()) return;
			// The tab that switched owns the save.
			applyLanguage(event.newValue, { persist: false });
		}

		window.addEventListener("storage", followOtherTab);
		return () => window.removeEventListener("storage", followOtherTab);
	}, [enabled]);

	return null;
}
